import { useMemo } from 'react'
import { Box, Flex } from '@radix-ui/themes'
import { MarkingSchemeType } from '@/types/question.type'
import { Levels } from '../../constant'

const MarkingScheme = ({
  markingScheme,
  isEditing = false,
  cantEditMark = false,
  onChange,
}: {
  markingScheme: MarkingSchemeType[]
  isEditing?: boolean
  isParagraph?: boolean
  cantEditMark?: boolean
  onChange?: (updated: MarkingSchemeType[]) => void
}) => {
  const headers = ['Level', 'Marks', 'Description', 'Rubric']
  const isBlank = (v?: string) => (v ?? '').trim().length === 0

  // Function to validate marking scheme level ordering for min-max format only
  const validateMarkingScheme = (scheme: MarkingSchemeType[]): string[] => {
    const errors: string[] = []

    // Ignore any level where both mark_range and description are blank
    const filtered = scheme.filter(
      (s) =>
        !(isBlank(s.mark_range) && isBlank(s.description)) &&
        s.level !== null &&
        s.level !== undefined
    )
    if (filtered.length === 0) return errors
    const sortedScheme = [...filtered].sort(
      (a, b) => (a.level || 0) - (b.level || 0)
    )

    // Parse helpers - only allow min-max format
    const parseRange = (
      val?: string
    ): { type: 'range'; min: number; max: number } | null => {
      const value = (val || '').trim()
      if (!value) return null
      // Only allow min-max format, not single numbers
      const parts = value.split('-')
      if (parts.length !== 2) return null // Must be min-max format
      const minStr = parts[0].trim()
      const maxStr = parts[1].trim()
      if (!/^\d+$/.test(minStr) || !/^\d+$/.test(maxStr)) return null
      const min = parseInt(minStr, 10)
      const max = parseInt(maxStr, 10)
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null
      return { type: 'range', min, max }
    }

    // First, validate individual level ranges (allow equality; must be integers)
    for (const level of sortedScheme) {
      const parsed = parseRange(level.mark_range)
      if (!parsed) {
        if ((level.mark_range || '').trim().length > 0) {
          errors.push(
            `Level ${level.level}: invalid format. Use "min-max" format only (e.g., "1-5").`
          )
        }
        continue
      }
      if (parsed.type === 'range') {
        if (parsed.min > parsed.max) {
          errors.push(
            `Level ${level.level}: minimum mark (${parsed.min}) must be less than or equal to maximum mark (${parsed.max})`
          )
        }
      }
    }

    // Then, validate level ordering
    for (let i = 0; i < sortedScheme.length; i++) {
      const current = sortedScheme[i]
      const prev = i > 0 ? sortedScheme[i - 1] : undefined

      const currentParsed = parseRange(current.mark_range)
      const prevParsed = prev ? parseRange(prev.mark_range) : null

      // Compare with previous: allow equality; current min must be >= prev max (range format only)
      if (currentParsed && prevParsed && prev) {
        const prevMax = prevParsed.max
        const currentMin = currentParsed.min
        if (currentMin < prevMax) {
          errors.push(
            `Level ${current.level} minimum (${currentMin}) must be greater than or equal to Level ${prev.level} maximum (${prevMax})`
          )
        }
      }
    }

    console.log('Final validation errors:', errors)
    return errors
  }

  // Always display 4 levels

  const schemeData = useMemo(() => {
    // First, separate data with valid levels from data without levels
    const dataWithLevels = markingScheme.filter(
      (m) => m.level !== null && m.level !== undefined
    )
    const withoutQueue = markingScheme
      .filter((m) => m.level === null || m.level === undefined)
      .slice()

    return Levels.map((item) => {
      const numericLevel = Number(String(item.level).replace(/\D/g, ''))

      // First try to find exact level match
      let matched = dataWithLevels.find((m) => m.level === numericLevel)

      // If no exact match and we have no-level items, consume the next one
      if (!matched && withoutQueue.length > 0) {
        matched = withoutQueue.shift()
      }

      return {
        ...item,
        apiLevel: matched?.level ?? 0,
        level: numericLevel, // Use the actual level number from Levels constant
        range: matched?.mark_range ?? '',
        description: matched?.description ?? '',
        rubric: matched?.rubric ?? '',
      }
    })
  }, [markingScheme])

  // Get validation errors
  const currentErrors = validateMarkingScheme(markingScheme)
  console.log('Current errors to display:', currentErrors)
  const levelHasError = (level: number) =>
    currentErrors.some((msg) => msg.includes(`Level ${level}`))

  return (
    <div className='w-full'>
      <div className='w-full overflow-x-auto'>
        <table className='w-full border-collapse text-left text-[#101828]'>
          <thead>
            <tr className='text-sm text-[#002652]'>
              {headers.map((item) => (
                <th key={item} className='px-4 py-3 font-semibold'>
                  {item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='text-sm text-[#002652]'>
            {schemeData?.map((item) => (
              <tr
                key={item.level}
                className=''
                style={{
                  backgroundColor: item.bgColor,
                }}
              >
                <td className='px-4 py-3'>
                  {!item.apiLevel || isBlank(item.range) ? (
                    'N/A'
                  ) : (
                    <Flex direction={'column'} gap={'1'}>
                      Level {item.level}
                      <Box
                        style={{
                          backgroundColor: item.color,
                        }}
                        className='w-fit rounded-[6px] px-2 py-0.5 text-white'
                      >
                        {item.status}
                      </Box>
                    </Flex>
                  )}
                </td>
                <td className='align-center px-4 py-3'>
                  {isEditing && !cantEditMark ? (
                    <input
                      className={`w-full rounded-md border p-1 outline-none select-text ${
                        levelHasError(item.level)
                          ? 'border-red-500'
                          : 'border-[#C2DEFF]'
                      }`}
                      value={item.range}
                      placeholder='e.g., 0-40 or 80'
                      aria-invalid={levelHasError(item.level) || undefined}
                      data-invalid={
                        levelHasError(item.level) ? 'true' : 'false'
                      }
                      onChange={(e) => {
                        if (!onChange) return

                        // Filter input to only allow numbers, hyphens, and spaces
                        const filteredValue = e.target.value
                          .replace(/[^0-9\-\s]/g, '') // Remove everything except numbers, hyphens, and spaces
                          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                          .trim()

                        // Validate format: must be "min-max" format only
                        const fullPattern = /^\d+\s*-\s*\d+$/
                        if (filteredValue && !fullPattern.test(filteredValue)) {
                          // Allow valid partial inputs while typing
                          const partialPattern =
                            /^\d*$|^\d+\s*$|^\d+\s*-\s*$|^\d+\s*-\s*\d*$/
                          if (!partialPattern.test(filteredValue)) {
                            return // Don't update if it's not a valid partial input
                          }
                        }

                        const next = [...markingScheme]
                        const idx = next.findIndex(
                          (m) => m.level === item.level
                        )
                        if (idx >= 0) {
                          next[idx] = {
                            ...next[idx],
                            mark_range: filteredValue,
                          }
                        } else {
                          next.push({
                            marking_scheme_level_id: 0, // Temporary ID for new entries
                            level: item.level,
                            mark_range: filteredValue,
                            description: item.description || '',
                            rubric: item.rubric || '',
                          })
                        }
                        onChange(next)
                      }}
                      onKeyDown={(e) => {
                        // Prevent certain keys
                        if (e.key === '.' || e.key === ',') {
                          e.preventDefault()
                        }
                      }}
                    />
                  ) : (
                    <span className='select-text'>{item.range || 'N/A'}</span>
                  )}
                </td>
                <td className='align-center max-w-[300px] min-w-[300px] px-4 py-3'>
                  {isEditing && !cantEditMark ? (
                    <textarea
                      className={`min-h-[60px] w-full resize-y rounded-md border border-[#C2DEFF] p-1 outline-none select-text`}
                      rows={3}
                      onMouseDown={(e) => {
                        // Only stop propagation for specific interactions, not text selection
                        const target = e.target as HTMLTextAreaElement
                        if (target.selectionStart !== target.selectionEnd) {
                          return // User is selecting text, don't interfere
                        }
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        // Allow text selection clicks
                        const target = e.target as HTMLTextAreaElement
                        if (target.selectionStart !== target.selectionEnd) {
                          return // User is selecting text
                        }
                        e.stopPropagation()
                      }}
                      onFocus={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      value={item.description}
                      onChange={(e) => {
                        if (!onChange) return
                        const next = [...markingScheme]
                        const idx = next.findIndex(
                          (m) => m.level === item.level
                        )
                        if (idx >= 0) {
                          next[idx] = {
                            ...next[idx],
                            description: e.target.value,
                          }
                        } else {
                          next.push({
                            marking_scheme_level_id: 0, // Temporary ID for new entries
                            level: item.level,
                            mark_range: item.range || '',
                            description: e.target.value,
                            rubric: item.rubric || '',
                          })
                        }
                        onChange(next)
                      }}
                    />
                  ) : (
                    <div className='whitespace-pre-wrap select-text'>
                      {item.description || 'N/A'}
                    </div>
                  )}
                </td>
                <td className='align-center max-w-[300px] min-w-[300px] px-4 py-3'>
                  {isEditing ? (
                    <textarea
                      className={`min-h-[60px] w-full resize-y rounded-md border border-[#C2DEFF] p-1 outline-none select-text`}
                      rows={3}
                      onMouseDown={(e) => {
                        // Only stop propagation for specific interactions, not text selection
                        const target = e.target as HTMLTextAreaElement
                        if (target.selectionStart !== target.selectionEnd) {
                          return // User is selecting text, don't interfere
                        }
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        // Allow text selection clicks
                        const target = e.target as HTMLTextAreaElement
                        if (target.selectionStart !== target.selectionEnd) {
                          return // User is selecting text
                        }
                        e.stopPropagation()
                      }}
                      onFocus={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      value={item.rubric}
                      onChange={(e) => {
                        if (!onChange) return
                        const next = [...markingScheme]
                        const idx = next.findIndex(
                          (m) => m.level === item.level
                        )
                        if (idx >= 0) {
                          next[idx] = {
                            ...next[idx],
                            rubric: e.target.value,
                          }
                        } else {
                          next.push({
                            marking_scheme_level_id: 0, // Temporary ID for new entries
                            level: item.level,
                            mark_range: item.range || '',
                            description: item.description || '',
                            rubric: e.target.value,
                          })
                        }
                        onChange(next)
                      }}
                    />
                  ) : (
                    <div className='whitespace-pre-wrap select-text'>
                      {item.rubric || 'N/A'}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Display validation errors */}
      {currentErrors.length > 0 && (
        <div className='mt-3 rounded-md border border-red-200 bg-red-50 p-3'>
          <div className='flex items-start'>
            <div className='flex-shrink-0'>
              <svg
                className='h-5 w-5 text-red-400'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800'>
                Marking Scheme Validation Errors
              </h3>
              <div className='mt-2 text-sm text-red-700'>
                <ul className='list-inside list-disc space-y-1'>
                  {currentErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarkingScheme
